// Global packages and components
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import { ArrowRightIcon } from '@heroicons/react/24/outline'
import { Heading, Image } from '~/components'
import { getLinkType } from '~/utils'

// Types
import type { SanityButtonProps, SanityImage } from '~/types'

interface tabItem {
  title: string
  description?: string
  image?: SanityImage
  logo?: SanityImage
  button?: SanityButtonProps
}

interface ContentItemProps extends tabItem {
  hasLink: boolean
}

interface TabData {
  title: string
  items: tabItem[]
}

interface BlockItemTabsProps {
  title: string
  tabs: TabData[]
}

// Main export
export default function BlockItemTabs({ title, tabs }: BlockItemTabsProps) {
  // Panels stay mounted and are hidden instead, so every tab's content is in
  // the server rendered HTML rather than only the first
  return (
    <div className="container">
      {title && (
        <Heading
          className="mb-10 text-center"
          tag="h2"
          size="h4"
        >
          {title}
        </Heading>
      )}
      {tabs && tabs.length > 0 && (
        <TabGroup>
          <TabList className="mb-10 flex flex-wrap items-center justify-center">
            {tabs.map((tab, i) => (
              <Tab
                key={'tab-' + i}
                className="mx-1 mb-2 rounded-full border border-teal-green px-5 py-2 font-semibold data-[selected]:bg-teal-green data-[selected]:text-white lg:mx-2"
              >
                {tab.title}
              </Tab>
            ))}
          </TabList>
          <TabPanels className="mx-auto">
            {tabs.map((tab, i) => (
              <TabPanel
                key={'tabpanel-' + i}
                unmount={false}
              >
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
                  {tab.items?.map((item, j) => {
                    // Resolves to an empty string when no link has been added
                    const buttonUrl = getLinkType({
                      linkType: item.button?.linkType,
                      linkReference: item.button?.linkReference,
                      linkSlug: item.button?.linkSlug,
                      linkExternal: item.button?.linkExternal,
                    })
                    const className = 'rounded-3xl overflow-auto'

                    // Only items with a link become an anchor and show the
                    // arrow — check the resolved URL, as the button object
                    // can be present without a link
                    const WrapperComponent = buttonUrl ? 'a' : 'div'
                    const wrapperProps = buttonUrl ? { href: buttonUrl } : {}

                    return (
                      <WrapperComponent
                        key={'item-' + j}
                        className={className}
                        {...wrapperProps}
                      >
                        <ContentItem
                          {...item}
                          hasLink={!!buttonUrl}
                        />
                      </WrapperComponent>
                    )
                  })}
                </div>
              </TabPanel>
            ))}
          </TabPanels>
        </TabGroup>
      )}
    </div>
  )
}

const ContentItem = ({ hasLink, ...e }: ContentItemProps) => (
  <div className="flex flex-col gap-4 overflow-hidden bg-white px-4 pb-4">
    {e.image && (
      <div className="-mx-6 flex aspect-[20/9]">
        <Image
          imageSize="full"
          image={e.image}
        />
      </div>
    )}
    {e.logo && (
      <div className="max-w-40">
        <Image image={e.logo} />
      </div>
    )}
    {e.title && (
      <div>
        <Heading
          bold
          className="mb-0"
          tag={'p'}
          size={'h7'}
        >
          {e.title}
        </Heading>
      </div>
    )}
    {e.description && <div>{e.description}</div>}
    {hasLink && (
      <div className="ml-auto block rounded-full bg-vibrant-green p-2">
        <ArrowRightIcon className="size-4 text-neutral-900" />
      </div>
    )}
  </div>
)
